import {
  eeBriaSettingsService,
  type EeBriaSettings,
} from "@/app/services/generated/eebriasettingsService";
import { integrationsMenu } from "@/app/ui/navigation/integrationsMenu";
import { makeDeepRef } from "@/components/composables/reactivityTypes";
import type { IntegrationMeta } from "@/components/pages/integrations/composables/types";
import { EnumIntegrationStatus } from "@/components/pages/integrations/types";
import messages from "@/plugins/i18n";
import { computed, onMounted } from "vue";
import img from "@/assets/images/app/integrations/eebria.png";
import { withLoading } from "@/components/composables/componentTools";
import type { LocalDate } from "@js-joda/core";
import {
  EeBriaOrderFilter,
  eeBriaOrderService,
} from "@/app/services/generated/eebriaorderService";
import { fillFilterDates } from "@/store/modules/integrationErrorStore";

export function useEebriaIntegration() {
  const eebriaSettings = makeDeepRef<EeBriaSettings | null>(null);

  const integrationStatus = computed(() =>
    eebriaIsActivated.value
      ? EnumIntegrationStatus.active
      : EnumIntegrationStatus.inactive
  );

  const eebriaIsActivated = computed(
    () =>
      eebriaSettings.value !== null &&
      eebriaSettings.value.readOnly.username.length > 0 &&
      eebriaSettings.value.calculated.passwordLength > 0
  );

  const eebriaMeta = computed(
    (): IntegrationMeta => ({
      name: messages.eebria_title,
      description: messages.eebria_description,
      integrationRoute: integrationsMenu.eebria.viewLocation(),
      miniImg: img,
      img,
      url: messages.eebria_url,
      activated: eebriaIsActivated.value,
      status: integrationStatus.value,
    })
  );

  const refreshEebriaSettings = withLoading(async () => {
    eebriaSettings.value = await eeBriaSettingsService.getEeBriaSettings();
  });

  onMounted(refreshEebriaSettings);

  return {
    integrationStatus,
    eebriaIsActivated,
    eebriaMeta,
    refreshEebriaSettings,
    eebriaSettings,
  };
}

export async function getEebriaOrders(
  fromDate?: LocalDate,
  toDate?: LocalDate
) {
  return (
    await eeBriaOrderService.getEebriaOrdersByFilter(
      fillFilterDates(new EeBriaOrderFilter(), fromDate, toDate)
    )
  ).orders;
}
